import { useEffect, useState } from 'react';

/**
 * Tracks whether a new version of the app has been deployed.
 *
 * The current build's version is injected at build time via Vite's `define`
 * as `__APP_VERSION__` (the short git SHA). A lightweight HTTP check against
 * `/version.json` (written during CI by cloudbuild.yaml) runs on mount and
 * every 5 minutes to detect a mismatch, which means a fresh deployment exists.
 *
 * Returns `{ updateAvailable, currentVersion, latestVersion }` so the caller
 * can render a banner, badge, or toast.
 */
export function useAppVersion() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  const currentVersion = (typeof __APP_VERSION__ === 'string' && __APP_VERSION__)
    ? __APP_VERSION__
    : /* fallback for dev without the global */ 'dev';

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      // Skip polling when tab is backgrounded — saves battery/radio wake
      if (document.visibilityState !== 'visible') return;

      // Suppress re-checking for 15s after a SW-triggered reload
      // to avoid the banner re-appearing before the new SW activates.
      const reloadedAt = sessionStorage.getItem('update_reload_at');
      if (reloadedAt) {
        const elapsed = Date.now() - parseInt(reloadedAt, 10);
        if (elapsed < 15_000) return;
        sessionStorage.removeItem('update_reload_at');
      }

      try {
        const res = await fetch(`/version.json?t=${Date.now()}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.version || data.version === 'dev') return;
        if (!cancelled) {
          setLatestVersion(data.version);
          if (data.version !== currentVersion) {
            setUpdateAvailable(true);
          }
        }
      } catch {
        // offline — skip
      }
    };

    check();
    // Poll every 15 min (reduced from 5 min to limit battery/radio wake)
    const interval = setInterval(check, 15 * 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [currentVersion]);

  return { updateAvailable, currentVersion, latestVersion };
}
